import { Component, Property } from '@wonderlandengine/api';

const TRUNK_H   = 4;   // solid voxels above terrain surface per tree trunk
const TRUNK_MAT = -1;  // negative sentinel: solid for collision, invisible in mesh

/**
 * TreeHandler
 * Generates and manages 30 distinct tree types across 5 climate biomes.
 * Hooks directly into the VoxelTreadmill pipeline.
 */
export class TreeHandler extends Component {
    static TypeName = 'tree-handler';

    static Properties = {
        treadmillObject:  Property.object(),
        treeDensity:      Property.float(0.06),
        /* One container object per biome — parent your tree prefabs as children */
        temperateGroup:   Property.object(),
        taigaGroup:       Property.object(),
        desertGroup:      Property.object(),
        jungleGroup:      Property.object(),
        alienGroup:       Property.object(),
    };

    init() {
        console.log('[TreeHandler] Initializing...');
        this._treadmill = null;
        // Destroy any WLE objects that survived a hot-reload / re-init
        if (this._spawnedTreesByChunk) {
            for (const records of this._spawnedTreesByChunk.values()) {
                for (const rec of records) {
                    const obj = rec && rec.obj ? rec.obj : rec;
                    if (obj && !obj.isDestroyed) obj.destroy();
                }
            }
        }
        // chunkKey → [{ obj, voxels:[{chunkId,mutKey,worldKey}], chunkKey }]
        this._spawnedTreesByChunk = new Map();
        // worldKey "wx_wy_wz" → { record, chunkKey }  (fast mine lookup)
        this._voxelToTree         = new Map();
        this._biomeMatrix = {};
    }

    // Maps actual WorldSampler biome IDs to this handler's 5 tree categories
    static _BIOME_CATEGORY = {
        grasslands:       'temperate',
        flesh_hive:       'temperate',
        ancient_ruins:    'desert',
        shattered_realm:  'temperate',
        tundra:           'taiga',
        fallen_city:      'taiga',
        alien_jungle:     'jungle',
        fungal_cathedral: 'jungle',
        canyon:           'none',
        volcanic:         'desert',
        cyber_grid:       'alien',
        // treeless biomes
        void_archipelago: 'none',
        neon_abyss:       'none',
    };

    start() {
        if (!this.treadmillObject) {
            console.error('[TreeHandler] Missing treadmillObject configuration. Deactivating.');
            this.active = false;
            return;
        }

        this._treadmill = this.treadmillObject.getComponent('voxel-treadmill');
        if (!this._treadmill) {
            console.error('[TreeHandler] Voxel Treadmill component not found on target object. Deactivating.');
            this.active = false;
            return;
        }

        // Read tree prefabs from the per-biome group objects
        const groups = {
            temperate: this.temperateGroup,
            taiga:     this.taigaGroup,
            desert:    this.desertGroup,
            jungle:    this.jungleGroup,
            alien:     this.alienGroup,
        };

        for (const [biome, groupObj] of Object.entries(groups)) {
            this._biomeMatrix[biome] = [];
            if (!groupObj) { console.warn(`[TreeHandler] No group object set for biome: ${biome}`); continue; }
            for (const child of groupObj.children) {
                child.active = false; // hide prefab templates
                this._biomeMatrix[biome].push(child);
            }
        }

        console.log('[TreeHandler] Biome matrix ready:', Object.fromEntries(
            Object.entries(this._biomeMatrix).map(([k, v]) => [k, v.length])
        ));

        // Expose globally so stream.js _applyDamageToVoxel can forward mine events
        try { window.TreeHandler = this; } catch (_) {}

        // Inject callbacks into the VoxelTreadmill's runtime pipeline
        const originalBuildChunk = this._treadmill._buildChunk || this._treadmill.buildChunk;

        if (originalBuildChunk) {
            const self = this;
            const targetMethod = this._treadmill._buildChunk ? '_buildChunk' : 'buildChunk';
            console.log(`[TreeHandler] Hooking into treadmill.${targetMethod}`);
            this._treadmill[targetMethod] = function(cx, cz, ...args) {
                const result = originalBuildChunk.call(this, cx, cz, ...args);
                self.generateTreesForChunk(cx, cz);
                return result;
            };
        }

        if (this._treadmill._destroyChunk) {
            const self = this;
            const originalDestroyChunk = this._treadmill._destroyChunk.bind(this._treadmill);
            console.log('[TreeHandler] Hooking into treadmill._destroyChunk');
            this._treadmill._destroyChunk = function(id, chunk) {
                const parts = id.split(',');
                self.clearTreesForChunk(parseInt(parts[0], 10), parseInt(parts[1], 10));
                return originalDestroyChunk(id, chunk);
            };
        }
    }

    /**
     * Generates trees for a chunk. For each tree, writes TRUNK_H solid voxels
     * into the treadmill mutation map so the trunk blocks movement. Trees whose
     * trunk was previously mined (mutation value 0) are skipped so destroyed
     * trees stay gone across chunk reloads.
     */
    generateTreesForChunk(cx, cz) {
        const chunkKey = `${cx},${cz}`;
        if (this._spawnedTreesByChunk.has(chunkKey)) return;

        const sampler = this._treadmill._sampler;
        if (!sampler) return;
        const cs = this._treadmill.chunkSize || 16;

        const chunkRecords = [];

        let seed = (((cx & 0xFFFF) | ((cz & 0xFFFF) << 16)) >>> 0) || 1;
        const random = () => {
            seed = (Math.imul(1664525, seed) + 1013904223) >>> 0;
            return seed / 0x100000000;
        };

        for (let lx = 0; lx < cs; lx += 2) {
            for (let lz = 0; lz < cs; lz += 2) {
                if (random() > this.treeDensity) continue;

                const worldX  = cx * cs + lx + (random() * 1.6);
                const worldZ  = cz * cs + lz + (random() * 1.6);
                const wx      = Math.round(worldX);
                const wz      = Math.round(worldZ);
                const surfY   = sampler.sampleHeight(wx, wz);
                if (surfY < 0) continue; // below sea level (h=0) — terrain is water-coloured, no trees

                const groundY = surfY + 1;
                const baseY   = groundY; // trunk starts one above terrain surface

                const biomeInfo = sampler.dominantBiomeAt(worldX, worldZ);
                if (!biomeInfo || !biomeInfo.id) continue;
                const category = TreeHandler._BIOME_CATEGORY[biomeInfo.id];
                if (!category || category === 'none') continue;
                const availableTrees = this._biomeMatrix[category];
                if (!availableTrees || availableTrees.length === 0) continue;

                const targetPrefab = availableTrees[Math.floor(random() * availableTrees.length)];
                if (!targetPrefab) continue;

                // Build trunk voxel list. Skip tree if any trunk voxel was previously mined.
                const voxels  = [];
                let   blocked = false;
                for (let dy = 0; dy < TRUNK_H; dy++) {
                    const wy    = baseY + dy;
                    const tcx   = Math.floor(wx / cs);
                    const tcz   = Math.floor(wz / cs);
                    const cid   = `${tcx},${tcz}`;
                    const loclx = ((wx % cs) + cs) % cs;
                    const loclz = ((wz % cs) + cs) % cs;
                    const mkey  = `${loclx}_${wy}_${loclz}`;
                    const wkey  = `${wx}_${wy}_${wz}`;
                    if (this._treadmill._mutations.get(cid, mkey) === 0) { blocked = true; break; }
                    voxels.push({ chunkId: cid, mutKey: mkey, worldKey: wkey });
                }
                if (blocked) continue;

                // Write solid trunk voxels into the mutation map
                const toRebuild = new Set();
                for (const v of voxels) {
                    this._treadmill._mutations.getChunk(v.chunkId)[v.mutKey] = TRUNK_MAT;
                    toRebuild.add(v.chunkId);
                }

                // Clone tree mesh as child of treadmill root so it moves with terrain
                const treeObject = targetPrefab.clone(this.treadmillObject);
                treeObject.active = true;
                treeObject.setPositionLocal([worldX, groundY - 1.25, worldZ]);

                const record = { obj: treeObject, voxels, chunkKey };
                for (const v of voxels) this._voxelToTree.set(v.worldKey, { record, chunkKey });
                chunkRecords.push(record);

                // Second rebuild so trunk mutations appear in the mesh.
                // (_buildChunk already ran _rebuildChunk before this hook fired.)
                for (const cid of toRebuild) {
                    try { this._treadmill._rebuildChunk(cid); } catch (_) {}
                }
            }
        }

        this._spawnedTreesByChunk.set(chunkKey, chunkRecords);
    }

    /**
     * Unloads trees when their chunk is discarded. Removes non-mined trunk
     * mutations so the terrain regenerates cleanly. Mined voxels (value 0)
     * are preserved so the destroyed state persists across chunk reloads.
     */
    clearTreesForChunk(cx, cz) {
        const chunkKey = `${cx},${cz}`;
        const records  = this._spawnedTreesByChunk.get(chunkKey);
        if (!records) return;

        for (const record of records) {
            for (const v of record.voxels) {
                const mods = this._treadmill._mutations.peekChunk(v.chunkId);
                if (mods && mods[v.mutKey] === TRUNK_MAT) delete mods[v.mutKey];
                this._voxelToTree.delete(v.worldKey);
            }
            if (record.obj && !record.obj.isDestroyed) record.obj.destroy();
            // No _rebuildChunk — the chunk is being destroyed
        }

        this._spawnedTreesByChunk.delete(chunkKey);
    }

    /**
     * Called by VoxelTreadmill (_applyDamageToVoxel) when a voxel is destroyed.
     * wx/wy/wz are world integer coordinates.
     */
    notifyVoxelRemoved(wx, wy, wz) {
        const wkey  = `${Math.floor(wx)}_${Math.floor(wy)}_${Math.floor(wz)}`;
        const entry = this._voxelToTree.get(wkey);
        if (!entry) return;

        const { record, chunkKey } = entry;
        const toRebuild = new Set();

        // Zero out all remaining trunk voxels so the trunk becomes air.
        // The hit voxel was already set to 0 by _applyDamageToVoxel.
        for (const v of record.voxels) {
            if (v.worldKey !== wkey) {
                const mods = this._treadmill._mutations.peekChunk(v.chunkId);
                if (mods) mods[v.mutKey] = 0;
                try { this._treadmill._queueChunkWrite(v.chunkId); } catch (_) {}
            }
            this._voxelToTree.delete(v.worldKey);
            toRebuild.add(v.chunkId);
        }

        // Rebuild terrain mesh so trunk voxels disappear visually
        for (const cid of toRebuild) {
            try { this._treadmill._rebuildChunk(cid); } catch (_) {}
        }

        // Destroy the tree mesh
        if (record.obj && !record.obj.isDestroyed) record.obj.destroy();

        // Remove from spawned list
        const records = this._spawnedTreesByChunk.get(chunkKey);
        if (records) {
            const idx = records.indexOf(record);
            if (idx !== -1) records.splice(idx, 1);
        }
    }
}